import { createSelector } from 'reselect';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';
import { getProgramEvents } from './getProgramEvents';
import { getIsSetupModeActive } from './getIsSetupModeActive';
import { getOpenLaborTicketRefs } from './getOpenLaborTicketRefs';
import { getCurrentWorkOrderOp } from './getCurrentWorkOrderOp';

/**
 * Part count multiplier of the active APM ERP run, read from the program-events
 * feed — the same server-authoritative source the grid and Production Run
 * Details drawer use.
 *
 * Why the feed (not activitySets.byRef): in the APM ERP flow, byRef is
 * wholesale-overwritten by a labor-ticket submit with server data that omits the
 * operator override, and its activity set can carry a STALE workOrderOperationRef
 * (the "latest activity" set, not the run the operator is watching). Both bugs
 * made the card show 1x / the wrong run. The feed is refetched on
 * apm:opdash-refresh, carries partCountMultiplier per event, and is keyed on the
 * same WOO the grid/drawer/server all agree on.
 *
 * Join key: the current work-order-op elected from the feed
 * (currentWorkOrderOp.workOrderOperationRef). We match the event with THAT WOO,
 * so the card reads the exact event the drawer reads. If there is no current WOO
 * yet (election still "None"), fall back to the most-recent visible event so a
 * multiplier can still surface.
 *
 * Visibility matches useProgramEventDisplayState / getApmCurrentOrderQuantity:
 * skip closed events and confirmed events whose labor ticket is no longer open.
 *
 * Returns null for non-APM flows, during setup mode, and when no matching event
 * carries a multiplier (callers fall back to their existing PCM source).
 */
export const getApmCurrentPartCountMultiplier = createSelector(
  [
    getIsApmERPJobFlow,
    getProgramEvents,
    getIsSetupModeActive,
    getOpenLaborTicketRefs,
    getCurrentWorkOrderOp,
  ],
  (
    isApmERPJobFlow,
    programEvents,
    isSetupModeActive,
    openLaborTicketRefs,
    currentWorkOrderOp
  ) => {
    if (!isApmERPJobFlow || isSetupModeActive) {
      return null;
    }

    const openLtRefSet = new Set(openLaborTicketRefs);

    const visibleEvents = programEvents
      .filter((event) => {
        if (event.closedDate) return false;
        if (event.confirmedAt && event.laborTicketRef) {
          return openLtRefSet.has(event.laborTicketRef);
        }
        return true;
      })
      .slice()
      .sort((a, b) => {
        const aTime = a.eventTime ? new Date(a.eventTime).getTime() : 0;
        const bTime = b.eventTime ? new Date(b.eventTime).getTime() : 0;
        return bTime - aTime;
      });

    // Prefer the event for the run the grid/drawer/server agree is current;
    // fall back to the most recent visible event when no WOO is elected yet.
    const currentWooRef = currentWorkOrderOp?.workOrderOperationRef;
    const matchedEvent =
      currentWooRef != null
        ? visibleEvents.find((event) => {
            return (
              String(event.workOrderOperationRef) === String(currentWooRef)
            );
          })
        : null;
    const activeEvent = matchedEvent ?? visibleEvents[0];

    if (!activeEvent || activeEvent.partCountMultiplier == null) {
      return null;
    }

    return activeEvent.partCountMultiplier;
  }
);
